import {
  ButtonElementTypes,
  ButtonSizeXL,
  ButtonSkinTertiary,
} from '@picsart/design-system/components/Button';
import { useEffect, useMemo, useState } from 'react';

import { analyticModule } from 'constants/main';
import useDevice from 'hooks/useDeviceType';
import useLandingPropsStore from 'stores/landingPropsStore';

import withAuthModal from 'redesignedComponents/decorators/withAuthModal';
import type { LinkButtonProps } from 'redesignedComponents/molecules/CTA/LinkButton';
import { linkButtonTestId } from 'redesignedComponents/organisms/CTA/testIds';
import { getButtonOption } from 'redesignedComponents/organisms/CTA/utils';

import ButtonWrapper from '../ButtonWrapper';
import { ButtonActionOptions } from './enums';

const LinkButton = ({
  label,
  mobileLabel,
  url,
  target,
  mobileUrl,
  iosMobileUrl,
  androidMobileUrl,
  onClick,
  buttonAction = ButtonActionOptions.Product,
  withWhiteBg,
  dataTestId,
  ariaLabel,
  cmsName,
  cmsId,
  className,
  startIcon,
  endIcon,
  buttonColorOption,
  index,
  qrModalTitle,
  tookUrlFromCampaign,
}: LinkButtonProps) => {
  const screenName = useLandingPropsStore(x => x.screenName);
  const category = useLandingPropsStore(x => x.category);
  const { isDesktop, isIOS, isAndroid } = useDevice();
  const [buttonUrl, setButtonUrl] = useState(url);

  const buttonOptions = getButtonOption(buttonColorOption);

  // Web To Mobile Editor Experiment
  const webToMobile = localStorage.getItem('webToMobile');

  useEffect(() => {
    if (!webToMobile) {
      let buttonLink = !isDesktop && mobileUrl ? mobileUrl : url;
      if (isIOS && iosMobileUrl) {
        buttonLink = iosMobileUrl;
      }

      if (isAndroid && androidMobileUrl) {
        buttonLink = androidMobileUrl;
      }
      setButtonUrl(buttonLink);
    }
  }, [androidMobileUrl, iosMobileUrl, isAndroid, isDesktop, isIOS, mobileUrl, url, webToMobile]);

  const buttonLabel = !isDesktop && mobileLabel ? mobileLabel : label;

  const analyticsProps = useMemo(
    () => ({
      category,
      action_button: buttonAction,
      screen_name: screenName,
      module: analyticModule,
      item_name: `${cmsName}/${label}`,
    }),
    [buttonAction, category, cmsName, label, screenName],
  );

  if (((isDesktop && !url) || !buttonUrl) && !qrModalTitle && !tookUrlFromCampaign) return null;

  return (
    <ButtonWrapper
      data-pulse-name={label}
      data-testid={dataTestId || linkButtonTestId}
      variant={buttonOptions.variant}
      tag={ButtonElementTypes.Link}
      skin={withWhiteBg ? ButtonSkinTertiary : buttonOptions.skin}
      size={ButtonSizeXL}
      href={buttonUrl}
      target={target}
      onClick={onClick}
      buttonLabel={buttonLabel}
      analyticsProps={analyticsProps}
      aria-label={ariaLabel}
      className={className}
      cmsId={cmsId}
      startIcon={startIcon}
      endIcon={endIcon}
      index={index}
      buttonColorOption={buttonColorOption}
      tookUrlFromCampaign={tookUrlFromCampaign}
    />
  );
};

export default withAuthModal<LinkButtonProps>(LinkButton);
